import type { TankMotion } from '@/types/spray';

/**
 * Reading how a tank load was driven.
 *
 * The rate check says a load went out light or heavy. A sprayer with no rate
 * controller puts out a fixed flow whatever the ground speed, so
 *
 *     L/ha = flow × 600 / (speed × width)
 *
 * and a load that is off is off because of one of those three. The server
 * measures each from the track (`sprayTankMotion`); this says which one moved.
 *
 * Diagnosis, not a verdict: nothing here blocks or flags a spray.
 */

export type MotionTone = 'ok' | 'watch' | 'unknown';

/** Past this a speed or flow is worth naming. Matches the rate check's band. */
const SPEED_FLOW_WATCH_PCT = 10;

/** Width is noisier — per-fix phone GPS is a couple of metres against 4 m rows. */
const WIDTH_WATCH_PCT = 15;

/**
 * Past this gap between what speed and flow predict and what the GPS area
 * says, the area is the likelier fault. It counts cells within 15 m of a fix,
 * and with fixes 10 s apart (30 s on sprays recorded before 2026-09-29) that
 * thins out in ways the sprayer does not.
 */
const AREA_DISAGREES_PCT = 15;

/**
 * Below this share of a load's pumping time, turning is not worth a sentence.
 * Every job turns; it matters when it is a real slice of the tank.
 */
const TURN_SHARE_WORTH_NAMING = 0.1;

/**
 * Water a load put out while turning at the row ends with the sprayer on.
 *
 * The implied flow is the load's water over spraying plus turning time, so
 * flow × turning minutes is that slice of the tank. It lands on the
 * headlands, outside the block outlines the area is measured over — so a
 * load that went on at the calibration in the rows still reads heavy per
 * hectare of block, by (spraying + turning) / spraying.
 *
 * Null when the sprayer was off for turns, or nothing can be worked out.
 */
export function turnWater(m: TankMotion): { litres: number; share: number } | null {
  const turning = m.turning_sec ?? 0;
  if (m.sprayer_off_on_turns || turning <= 0 || !(m.spraying_sec > 0) || m.implied_flow_lpm == null) {
    return null;
  }
  return {
    litres: m.implied_flow_lpm * turning / 60,
    share: turning / (m.spraying_sec + turning),
  };
}

/**
 * The rate variance the block's area should show, given how the load was
 * driven: what speed, flow and width predict in the rows, scaled up by the
 * water that went on turning.
 */
function predictedOverBlock(m: TankMotion): number | null {
  const p = m.predicted_rate_variance_pct;
  if (p == null) return null;
  const t = turnWater(m);
  if (!t) return p;
  return ((1 + p / 100) / (1 - t.share) - 1) * 100;
}

/** "about 590 L" — tens of litres is as close as a turning estimate gets. */
function litres(l: number): string {
  return `about ${(Math.round(l / 10) * 10).toLocaleString('en-AU')} L`;
}

export interface MotionFact {
  label: string;
  value: string;
  /** Against the calibration, where there is one to hold it against. */
  compare: string | null;
  tone: MotionTone;
}

export interface MotionReading {
  facts: MotionFact[];
  /** Which of speed, flow and width explains the load — or that none does. */
  findings: string[];
}

function signed(pct: number): string {
  return `${pct > 0 ? '+' : ''}${pct.toFixed(0)}%`;
}

function toneFor(pct: number | null, limit: number): MotionTone {
  if (pct == null) return 'unknown';
  return Math.abs(pct) > limit ? 'watch' : 'ok';
}

function minutes(sec: number): string {
  const m = Math.round(sec / 60);
  return m < 1 ? '<1 min' : `${m} min`;
}

/** "about every second row" for a pass spacing near a whole number of rows. */
function rowPattern(ratio: number): string | null {
  const whole = Math.round(ratio);
  if (whole < 2 || whole > 6 || Math.abs(ratio - whole) > 0.3) return null;
  const words = ['', '', 'second', 'third', 'fourth', 'fifth', 'sixth'];
  return `about every ${words[whole]} row`;
}

/**
 * What a load's driving has to say.
 *
 * @param m - the load's motion
 * @param measuredRateVariancePct - the rate check's own variance, off the GPS
 *   area. When speed and flow predict something quite different, that is
 *   worth saying: the area is then the likelier fault, not the sprayer.
 * @param rateExcludesTurns - the check already left the turning water out
 *   (`turn_water_l` on the load), so it is a rate in the rows. An older check
 *   divided the whole tank over the block.

 * @returns null when the track gave nothing at all to read
 */
export function readTankMotion(
  m: TankMotion,
  measuredRateVariancePct: number | null = null,
  rateExcludesTurns = false,
): MotionReading | null {
  if (m.median_speed_kph == null && m.implied_flow_lpm == null && m.pass_spacing_m == null) {
    return null;
  }

  const facts: MotionFact[] = [];
  const findings: string[] = [];

  if (m.median_speed_kph != null) {
    const spread = m.p10_speed_kph != null && m.p90_speed_kph != null
      && m.p90_speed_kph - m.p10_speed_kph >= 0.5
      ? ` (${m.p10_speed_kph.toFixed(1)}–${m.p90_speed_kph.toFixed(1)})`
      : '';
    facts.push({
      label: 'Speed',
      value: `${m.median_speed_kph.toFixed(1)} km/h${spread}`,
      compare: m.expected_speed_kph != null && m.speed_variance_pct != null
        ? `calibrated ${m.expected_speed_kph.toFixed(1)} · ${signed(m.speed_variance_pct)}`
        : null,
      tone: toneFor(m.speed_variance_pct, SPEED_FLOW_WATCH_PCT),
    });
  }

  if (m.implied_flow_lpm != null) {
    facts.push({
      label: 'Flow',
      value: `${m.implied_flow_lpm.toFixed(0)} L/min`,
      compare: m.expected_flow_lpm != null && m.flow_variance_pct != null
        ? `calibrated ${m.expected_flow_lpm.toFixed(0)} · ${signed(m.flow_variance_pct)}`
        : null,
      tone: toneFor(m.flow_variance_pct, SPEED_FLOW_WATCH_PCT),
    });
  }

  // A weed-strip sprayer's passes are a row apart, but each wets only its
  // strip, so the gap between passes says whether rows were missed and
  // nothing about how much water the strip got.
  const strip = m.sprayed_width_m != null && m.sprayed_width_m > 0 ? m.sprayed_width_m : null;

  if (m.pass_spacing_m != null) {
    const unit = strip != null || (m.expected_pass_spacing_m != null && m.row_spacing_m != null
      && Math.abs(m.expected_pass_spacing_m - m.row_spacing_m) < 0.01)
      ? 'rows' : 'swath';
    facts.push({
      label: 'Pass width',
      value: `${m.pass_spacing_m.toFixed(1)} m`,
      compare: m.expected_pass_spacing_m != null && m.width_variance_pct != null
        ? `${m.expected_pass_spacing_m.toFixed(1)} m ${unit} · ${signed(m.width_variance_pct)}`
        : null,
      tone: toneFor(m.width_variance_pct, WIDTH_WATCH_PCT),
    });
  }

  if (m.spraying_sec > 0) {
    const turning = m.turning_sec ?? 0;
    const notes = [
      turning >= 60
        ? `${minutes(turning)} turning at row ends${m.sprayer_off_on_turns ? ' (sprayer off, not counted)' : ''}`
        : null,
      m.stopped_sec >= 60 ? `${minutes(m.stopped_sec)} stopped in the block` : null,
    ].filter(Boolean);
    facts.push({
      label: 'Time',
      value: `${minutes(m.spraying_sec)} spraying`,
      compare: notes.length ? notes.join(' · ') : null,
      tone: 'unknown',
    });
  }

  // --- What explains it ---------------------------------------------------
  const sv = m.speed_variance_pct;
  const fv = m.flow_variance_pct;
  const wv = m.width_variance_pct;

  if (sv != null && Math.abs(sv) > SPEED_FLOW_WATCH_PCT) {
    const rateEffect = (1 / (1 + sv / 100) - 1) * 100;
    findings.push(
      `Driven ${Math.abs(sv).toFixed(0)}% ${sv > 0 ? 'faster' : 'slower'} than the calibration — `
      + `on its own that puts out about ${Math.abs(rateEffect).toFixed(0)}% `
      + `${rateEffect < 0 ? 'less' : 'more'} water per hectare.`,
    );
  }
  if (fv != null && Math.abs(fv) > SPEED_FLOW_WATCH_PCT) {
    findings.push(
      `The water lasted as though it flowed ${Math.abs(fv).toFixed(0)}% ${fv > 0 ? 'faster' : 'slower'} `
      + 'than calibrated — '
      + (fv > 0
        ? 'pressure, PTO speed and nozzles are where to look, or the tank held less water than '
          + 'was entered, or some of it went on outside the tracked time.'
        : 'pressure, PTO speed and nozzles are where to look. Driving inside the block with the '
          + 'sprayer off also reads as slow flow.'),
    );
  }
  if (wv != null && Math.abs(wv) > WIDTH_WATCH_PCT && m.pass_spacing_m != null && m.expected_pass_spacing_m) {
    const pattern = wv > 0 ? rowPattern(m.pass_spacing_m / m.expected_pass_spacing_m) : null;
    findings.push(
      `Passes averaged ${m.pass_spacing_m.toFixed(1)} m apart against ${m.expected_pass_spacing_m.toFixed(1)} m`
      + `${pattern ? ` — ${pattern}` : ''}. `
      + (strip != null
        ? (wv > 0
          ? `The sprayer wets only ${strip.toFixed(1)} m per pass, so rows between passes were missed, not sprayed lighter.`
          : 'Some rows were driven twice, so their strip got sprayed twice.')
        : wv > 0
          ? 'Each pass is spreading its water over more ground than the calibration assumed, or rows were missed.'
          : 'Passes overlap, so the same ground is getting water twice.'),
    );
  }

  const judged = [sv, fv].every(v => v != null);
  if (!findings.length && judged) {
    findings.push(
      m.pass_spacing_m != null
        ? 'Speed, flow and pass width all match the calibration.'
        : 'Speed and flow both match the calibration.',
    );
  }

  // Water sprayed while turning is pumped but lands outside the block, so the
  // load reads heavy over the block's area however well the rows went.
  const turn = turnWater(m);
  const overBlock = predictedOverBlock(m);
  if (turn && turn.share >= TURN_SHARE_WORTH_NAMING && rateExcludesTurns) {
    findings.push(
      `${litres(turn.litres)} (${Math.round(turn.share * 100)}% of the load) went on while turning `
      + 'at the row ends with the sprayer on. It lands on the headlands, so it is left out of '
      + "this tank's rate. If the sprayer is shut off for turns, tick 'Sprayer off during turns' on the tank.",
    );
  } else if (turn && turn.share >= TURN_SHARE_WORTH_NAMING && overBlock != null) {
    findings.push(
      `${litres(turn.litres)} (${Math.round(turn.share * 100)}% of the load) went on while turning `
      + 'at the row ends with the sprayer on. That lands on the headlands, not in the block, so '
      + `the load reads about ${signed(overBlock)} over the block's area`
      + (m.predicted_rate_variance_pct != null
        ? ` even though the rows got ${signed(m.predicted_rate_variance_pct)}`
        : '')
      + '. If the sprayer is shut off for turns, tick "Sprayer off during turns" on the tank.',
    );
  }

  // Speed and flow are measured directly; the GPS area is inferred from the
  // track. When they tell different stories, say which to trust.
  const expectedByArea = rateExcludesTurns ? m.predicted_rate_variance_pct : overBlock;
  if (expectedByArea != null && measuredRateVariancePct != null
    && Math.abs(expectedByArea - measuredRateVariancePct) > AREA_DISAGREES_PCT) {
    findings.push(
      `How it was driven predicts ${signed(expectedByArea)} on the rate, but the GPS area says `
      + `${signed(measuredRateVariancePct)}. The area figure is the likelier one to be off — `
      + 'it only counts ground near a GPS fix, and fixes are tens of metres apart. '
      + 'On a finished spray, Re-check tanks works the areas out again.',
    );
  }

  return { facts, findings };
}

/**
 * Water the whole job put out turning at the row ends with the sprayer on.
 * Null when no closed load turned with it on.
 */
export function jobTurnWater(tanks: TankMotion[]): { litres: number; tanks: number } | null {
  const turns = tanks.filter(t => !t.is_open).map(turnWater).filter(t => t != null && t.litres > 0);
  if (!turns.length) return null;
  return { litres: turns.reduce((s, t) => s + t!.litres, 0), tanks: turns.length };
}

/**
 * Loads in order with their speed, for spotting a drift across the day —
 * "tanks 1–3 at 7, the last two at 9" is the rushing-home pattern.
 * Only loads that yielded a speed are included.
 */
export function speedTrend(tanks: TankMotion[]): Array<{ tank: number; kph: number }> {
  return tanks
    .filter(t => t.median_speed_kph != null)
    .sort((a, b) => a.tank_number - b.tank_number)
    .map(t => ({ tank: t.tank_number, kph: t.median_speed_kph as number }));
}
